import React, { useState } from "react";
import Table, { TablePaginationConfig } from "antd/lib/table";
import "./table.scss";
import classNames from "classnames";
import { FilterValue, SorterResult, TableCurrentDataSource } from "antd/lib/table/interface";

export type TBasicTable = {
    columns: any;
    data: any[];
    rowKey: string;
    scroll?: boolean;
    scrollHeightY?: number;
    loading?: boolean;
    noDataText?: string;
    hasPagination?: boolean;
    paginationPageSize?: number;
    lightGreyOnEvenRows?: boolean;
    isCompact?: boolean;
    rowClassName?: string | ((arg0: any, arg1: number) => string);
    showPaginationSizeChanger?: boolean;
    onChange?: (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[],
        extra: TableCurrentDataSource<any>
    ) => void;
};

export const BasicTable = ({
    columns,
    data,
    rowKey,
    scroll,
    scrollHeightY,
    loading,
    noDataText,
    hasPagination = true,
    paginationPageSize = 15,
    lightGreyOnEvenRows = false,
    isCompact = false,
    rowClassName,
    showPaginationSizeChanger = true,
    onChange,
}: TBasicTable) => {
    const [currentPageSize, setCurrentPageSize] = useState(paginationPageSize);

    const className = classNames({
        "loading-table-data": loading,
        "light-grey-rows": lightGreyOnEvenRows,
        "compact-table": isCompact,
    });

    const handleOnChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter: SorterResult<any> | SorterResult<any>[],
        extra: TableCurrentDataSource<any>
    ) => {
        if (showPaginationSizeChanger && pagination.pageSize) {
            setCurrentPageSize(pagination.pageSize);
        }

        onChange?.(pagination, filters, sorter, extra);
    };

    const pageSizeOptions = [paginationPageSize, 15, 30, 50, 100]
        .filter((value, index, self) => {
            // Keep only unique values and values that are less than or equal to the length of the data plus an offset
            return self.indexOf(value) === index && value <= data.length + 20;
        })
        .sort((a, b) => a - b);

    const showPagination = hasPagination;

    return (
        <Table
            columns={columns}
            dataSource={data}
            pagination={
                showPagination && {
                    position: ["bottomRight"],
                    pageSize: currentPageSize,
                    showSizeChanger: showPaginationSizeChanger && pageSizeOptions.length > 1,
                    pageSizeOptions: pageSizeOptions,
                    locale: {
                        items_per_page: "",
                    },
                }
            }
            rowKey={rowKey}
            scroll={scroll ? { y: scrollHeightY } : undefined}
            className={className}
            data-empty-content={data.length > 0 ? "" : noDataText ?? "No data"}
            rowClassName={rowClassName}
            onChange={handleOnChange}
        />
    );
};

export default BasicTable;
